import type { Book, Chapter, Witness } from '../store/booksStore'
import type { WorkbenchProjectRecord } from '../types/workbenchProjects'
import type { WorkbenchWitnessAssignmentRecord } from '../types/workbenchWitnessAssignments'
import { workbenchOptionalText, workbenchSafeNumber, workbenchSafeText } from './workbenchSafeValue'

export interface WorkbenchProject {
  id: string
  name: string
  description?: string
}

export interface WorkbenchWork {
  id: string
  project_id: string
  source_book_id: string
  name: string
  description?: string
  order_index: number
  created_at: number
  last_modified: number
}

export interface WorkbenchContext {
  id: string
  project_id: string
  work_id: string
  source_book_id: string
  source_chapter_id: string
  name: string
  label_prefix?: string
  description?: string
  order_index: number
  context_type: 'chapter'
  created_at: number
}

export interface WorkbenchWitnessSource {
  id: string
  project_id: string
  source_book_id: string
  source_chapter_id: string
  sigla: string
  description: string
  library: string
  city?: string
  shelfmark?: string
  manuscript_date?: string
  witness_type?: Witness['witness_type']
  transcription_type?: Witness['transcription_type']
  coverage_status?: Witness['coverage_status']
  lacunose_status?: Witness['lacunose_status']
  processing_status: Witness['processing_status']
  created_at: number
  last_modified: number
  version: number
}

export interface WorkbenchProjectSnapshot {
  project: WorkbenchProject
  works: WorkbenchWork[]
  contextsByWorkId: Record<string, WorkbenchContext[]>
  witnessesByContextId: Record<string, WorkbenchWitnessSource[]>
  excludedWitnessesByContextId: Record<string, WorkbenchWitnessSource[]>
  unassignedWitnesses: WorkbenchWitnessSource[]
  witnessAssignmentsByWitnessId: Record<string, WorkbenchWitnessAssignmentRecord>
}

export interface WorkbenchRouteSelection {
  projectId?: string
  workId?: string
  contextId?: string
}

export const DEFAULT_WORKBENCH_PROJECT_ID = 'default-project'

export const mapBookToWorkbenchWork = (
  book: Book,
  options?: { projectId?: string }
): WorkbenchWork => ({
  id: workbenchSafeText(book.id, 'work'),
  project_id: workbenchSafeText(options?.projectId, DEFAULT_WORKBENCH_PROJECT_ID),
  source_book_id: workbenchSafeText(book.id, 'work'),
  name: workbenchSafeText(book.name, 'Untitled work'),
  description: workbenchOptionalText(book.description),
  order_index: 0,
  created_at: workbenchSafeNumber(book.created_at),
  last_modified: workbenchSafeNumber(book.last_modified)
})

export const mapChapterToWorkbenchContext = (
  chapter: Chapter,
  options?: { projectId?: string }
): WorkbenchContext => ({
  id: workbenchSafeText(chapter.id, 'context'),
  project_id: workbenchSafeText(options?.projectId, DEFAULT_WORKBENCH_PROJECT_ID),
  work_id: workbenchSafeText(chapter.book_id, 'work'),
  source_book_id: workbenchSafeText(chapter.book_id, 'work'),
  source_chapter_id: workbenchSafeText(chapter.id, 'context'),
  name: workbenchSafeText(chapter.name, 'Untitled context'),
  label_prefix: workbenchOptionalText(chapter.label_prefix),
  description: workbenchOptionalText(chapter.description),
  order_index: workbenchSafeNumber(chapter.order_index),
  context_type: 'chapter',
  created_at: workbenchSafeNumber(chapter.created_at)
})

export const mapWitnessToWorkbenchWitness = (
  witness: Witness,
  options?: { projectId?: string }
): WorkbenchWitnessSource => ({
  id: workbenchSafeText(witness.id, 'witness'),
  project_id: workbenchSafeText(options?.projectId, DEFAULT_WORKBENCH_PROJECT_ID),
  source_book_id: workbenchSafeText(witness.book_id, 'work'),
  source_chapter_id: workbenchSafeText(witness.chapter_id, 'context'),
  sigla: workbenchSafeText(witness.sigla, workbenchSafeText(witness.id, 'Witness')),
  description: workbenchSafeText(witness.description, workbenchSafeText(witness.id, 'Witness')),
  library: workbenchSafeText(witness.library),
  city: workbenchOptionalText(witness.city),
  shelfmark: workbenchOptionalText(witness.shelfmark),
  manuscript_date: workbenchOptionalText(witness.manuscript_date),
  witness_type: witness.witness_type,
  transcription_type: witness.transcription_type,
  coverage_status: witness.coverage_status,
  lacunose_status: witness.lacunose_status,
  processing_status: witness.processing_status || 'pending',
  created_at: workbenchSafeNumber(witness.created_at),
  last_modified: workbenchSafeNumber(witness.last_modified),
  version: workbenchSafeNumber(witness.version)
})

export const buildProjectSnapshotFromBooksState = ({
  books,
  chapters,
  witnesses,
  witnessAssignments = [],
  project,
  projectId = DEFAULT_WORKBENCH_PROJECT_ID,
  projectName = 'Default Project',
  projectDescription
}: {
  books: Book[]
  chapters: Chapter[]
  witnesses: Witness[]
  witnessAssignments?: WorkbenchWitnessAssignmentRecord[]
  project?: WorkbenchProjectRecord
  projectId?: string
  projectName?: string
  projectDescription?: string
}): WorkbenchProjectSnapshot => {
  const effectiveProjectId = workbenchSafeText(project?.id, projectId)
  const effectiveProjectName = workbenchSafeText(project?.name, projectName)
  const effectiveProjectDescription = workbenchOptionalText(project?.description) || workbenchOptionalText(projectDescription)
  const rawSourceBookIds = project?.source_book_ids
  const scopedBookIds = Array.isArray(rawSourceBookIds) && rawSourceBookIds.length
    ? new Set(rawSourceBookIds.map(bookId => workbenchSafeText(bookId)).filter(Boolean))
    : null

  const scopedBooks = scopedBookIds
    ? books.filter(book => scopedBookIds.has(book.id))
    : books

  const scopedChapters = scopedBookIds
    ? chapters.filter(chapter => scopedBookIds.has(chapter.book_id))
    : chapters

  const scopedWitnesses = scopedBookIds
    ? witnesses.filter(witness => scopedBookIds.has(witness.book_id))
    : witnesses
  const scopedAssignments = witnessAssignments
    .filter(assignment => assignment.project_id === effectiveProjectId)
    .reduce<Record<string, WorkbenchWitnessAssignmentRecord>>((accumulator, assignment) => {
      const current = accumulator[assignment.witness_id]
      if (!current || assignment.last_modified >= current.last_modified) {
        accumulator[assignment.witness_id] = assignment
      }
      return accumulator
    }, {})

  const works = scopedBooks.map(book => mapBookToWorkbenchWork(book, { projectId: effectiveProjectId }))

  const contextsByWorkId = scopedChapters.reduce<Record<string, WorkbenchContext[]>>((accumulator, chapter) => {
    const nextContext = mapChapterToWorkbenchContext(chapter, { projectId: effectiveProjectId })
    const existing = accumulator[nextContext.work_id]
    if (existing) {
      existing.push(nextContext)
    } else {
      accumulator[nextContext.work_id] = [nextContext]
    }
    return accumulator
  }, {})
  for (const workContexts of Object.values(contextsByWorkId)) {
    workContexts.sort((left, right) => left.order_index - right.order_index)
  }

  const witnessBuckets = scopedWitnesses.reduce<{
    witnessesByContextId: Record<string, WorkbenchWitnessSource[]>
    excludedWitnessesByContextId: Record<string, WorkbenchWitnessSource[]>
    unassignedWitnesses: WorkbenchWitnessSource[]
  }>((accumulator, witness) => {
    const nextWitness = mapWitnessToWorkbenchWitness(witness, { projectId: effectiveProjectId })
    const assignment = scopedAssignments[nextWitness.id]

    const appendToBucket = (
      buckets: Record<string, WorkbenchWitnessSource[]>,
      bucketId: string
    ) => {
      const existing = buckets[bucketId]
      if (existing) {
        existing.push(nextWitness)
      } else {
        buckets[bucketId] = [nextWitness]
      }
    }

    if (assignment) {
      if (!assignment.context_id) {
        accumulator.unassignedWitnesses.push(nextWitness)
        return accumulator
      }

      if (assignment.status === 'excluded') {
        appendToBucket(accumulator.excludedWitnessesByContextId, assignment.context_id)
        return accumulator
      }

      appendToBucket(accumulator.witnessesByContextId, assignment.context_id)
      return accumulator
    }

    appendToBucket(accumulator.witnessesByContextId, nextWitness.source_chapter_id)
    return accumulator
  }, {
    witnessesByContextId: {},
    excludedWitnessesByContextId: {},
    unassignedWitnesses: []
  })
  // Buckets are sorted once after bucketing instead of on every insertion.
  for (const bucket of Object.values(witnessBuckets.witnessesByContextId)) {
    bucket.sort((left, right) => left.created_at - right.created_at)
  }
  for (const bucket of Object.values(witnessBuckets.excludedWitnessesByContextId)) {
    bucket.sort((left, right) => left.created_at - right.created_at)
  }

  const knownContexts = Object.values(contextsByWorkId).flat()
  const knownContextIds = new Set(knownContexts.map(nextContext => nextContext.id))

  const witnessesByContextId = knownContexts
    .reduce<Record<string, WorkbenchWitnessSource[]>>((accumulator, nextContext) => {
      accumulator[nextContext.id] = witnessBuckets.witnessesByContextId[nextContext.id] || []
      return accumulator
    }, {})

  const excludedWitnessesByContextId = knownContexts
    .reduce<Record<string, WorkbenchWitnessSource[]>>((accumulator, nextContext) => {
      accumulator[nextContext.id] = witnessBuckets.excludedWitnessesByContextId[nextContext.id] || []
      return accumulator
    }, {})

  // Witnesses bucketed under a context that no longer exists (for example an
  // assignment pointing at a deleted chapter) must surface as unassigned
  // instead of silently dropping out of every Workbench list.
  const orphanedWitnesses = [
    ...Object.entries(witnessBuckets.witnessesByContextId),
    ...Object.entries(witnessBuckets.excludedWitnessesByContextId)
  ]
    .filter(([bucketContextId]) => !knownContextIds.has(bucketContextId))
    .flatMap(([, bucketWitnesses]) => bucketWitnesses)

  return {
    project: {
      id: effectiveProjectId,
      name: effectiveProjectName,
      description: effectiveProjectDescription
    },
    works,
    contextsByWorkId,
    witnessesByContextId,
    excludedWitnessesByContextId,
    unassignedWitnesses: [...witnessBuckets.unassignedWitnesses, ...orphanedWitnesses]
      .sort((left, right) => left.created_at - right.created_at),
    witnessAssignmentsByWitnessId: scopedAssignments
  }
}

export const buildProjectSnapshotsFromBooksState = ({
  projects,
  books,
  chapters,
  witnesses,
  witnessAssignments = []
}: {
  projects: WorkbenchProjectRecord[]
  books: Book[]
  chapters: Chapter[]
  witnesses: Witness[]
  witnessAssignments?: WorkbenchWitnessAssignmentRecord[]
}): WorkbenchProjectSnapshot[] => {
  if (!projects.length) {
    return [
      buildProjectSnapshotFromBooksState({
        books,
        chapters,
        witnesses,
        witnessAssignments,
        projectId: DEFAULT_WORKBENCH_PROJECT_ID,
        projectName: 'Project'
      })
    ]
  }

  return projects.map(project =>
      buildProjectSnapshotFromBooksState({
        books,
        chapters,
        witnesses,
        witnessAssignments,
        project
      })
  )
}

export const resolveWorkbenchSelection = (
  snapshot: WorkbenchProjectSnapshot,
  selection: WorkbenchRouteSelection
): {
  project: WorkbenchProject
  work: WorkbenchWork | null
  context: WorkbenchContext | null
} => {
  const projectMatches = !selection.projectId || selection.projectId === snapshot.project.id
  if (!projectMatches) {
    return {
      project: snapshot.project,
      work: null,
      context: null
    }
  }

  const context = selection.contextId
    ? Object.values(snapshot.contextsByWorkId).flat().find(candidate => candidate.id === selection.contextId) || null
    : null

  const work = selection.workId
    ? snapshot.works.find(candidate => candidate.id === selection.workId) || null
    : (context ? snapshot.works.find(candidate => candidate.id === context.work_id) || null : null)

  return {
    project: snapshot.project,
    work,
    context
  }
}
